/**
 * Patient-Context MedPass Workflow Component
 * 
 * Redesigned medication ordering workflow that operates within patient context.
 * Addresses all client feedback issues:
 * - No separate navigation tab
 * - Auto-populated patient information
 * - Streamlined 4-step process
 * - DOH-focused medication selection
 * - Simplified UI with reduced cognitive load
 * - Enhanced compliance and audit trails
 */

import React, { useState, useEffect } from 'react';
import {
    Steps,
    Card,
    Button,
    message,
    Alert,
    Row,
    Col,
    Progress,
    Space,
    Divider
} from 'antd';
import {
    UserOutlined,
    MedicineBoxOutlined,
    EditOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    SafetyCertificateOutlined
} from '@ant-design/icons';

import PatientVerificationStep from './PatientContextSteps/PatientVerificationStep';
import DOHMedicationSelectionStep from './PatientContextSteps/DOHMedicationSelectionStep';
import DosingInstructionsStep from './PatientContextSteps/DosingInstructionsStep';
import MonitoringReviewStep from './PatientContextSteps/MonitoringReviewStep';
import medpassService from '../../services/medpassService';
import patientService from '../../services/patientService';
import dayjs from 'dayjs';

const { Step } = Steps;

const PatientContextMedPassWorkflow = ({ patient, onComplete, onCancel }) => {
    // State management
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [workflowData, setWorkflowData] = useState({
        patient_context: {
            patient_id: patient?.id,
            mrn: patient?.mrn,
            full_name: patient?.full_name,
            date_of_birth: patient?.date_of_birth,
            age: patient?.age,
            gender: patient?.gender,
            allergies: patient?.allergies || [],
            medical_conditions: patient?.medical_conditions || [],
            current_medications: patient?.current_medications || [],
            verified: false
        },
        selected_medications: [],
        dosing_instructions: {},
        monitoring_requirements: {},
        workflow_id: null,
        safety_validated: false,
        physician_attestation: false
    });

    // Helper function to calculate default administration times based on frequency
    const getDefaultAdministrationTimes = (frequency) => {
        const timingMap = {
            'once daily': ['09:00'],
            'twice daily': ['09:00', '21:00'],
            'three times daily': ['08:00', '13:00', '18:00'],
            'four times daily': ['08:00', '13:00', '18:00', '22:00'],
            'once_daily': ['09:00'],
            'twice_daily': ['09:00', '21:00'],
            'three_times_daily': ['08:00', '13:00', '18:00'],
            'four_times_daily': ['08:00', '13:00', '18:00', '22:00'],
            'every_8_hours': ['08:00', '16:00', '00:00'],
            'every_6_hours': ['06:00', '12:00', '18:00', '00:00'],
            'every_4_hours': ['06:00', '10:00', '14:00', '18:00', '22:00', '02:00'],
            'every 4 hours': ['06:00', '10:00', '14:00', '18:00', '22:00', '02:00'],
            'every 6 hours': ['06:00', '12:00', '18:00', '00:00'],
            'every 8 hours': ['08:00', '16:00', '00:00'],
            'every 12 hours': ['09:00', '21:00'],
            'with meals': ['08:00', '13:00', '18:00'],
            'at bedtime': ['22:00'],
            'bedtime': ['22:00'],
            'as needed': ['09:00']
        };
        return timingMap[frequency] || ['09:00'];
    };

    // Step definitions - Redesigned based on feedback
    const steps = [
        {
            title: 'Patient Verification',
            key: 'patient_verification',
            icon: <UserOutlined />,
            description: 'Confirm patient identity and review safety information'
        },
        {
            title: 'DOH Medication Selection',
            key: 'medication_selection', 
            icon: <MedicineBoxOutlined />,
            description: 'Select medications from DOH-approved list'
        },
        {
            title: 'Dosing & Instructions',
            key: 'dosing_instructions',
            icon: <EditOutlined />,
            description: 'Define dosing regimen and physician instructions'
        },
        {
            title: 'Monitoring & Review',
            key: 'monitoring_review',
            icon: <FileTextOutlined />,
            description: 'Optional monitoring and final prescription review'
        }
    ];

    // Initialize workflow on mount
    useEffect(() => {
        initializeWorkflow();
    }, [patient]);

    const initializeWorkflow = async () => {
        try {
            // Create patient-context workflow
            const workflowPayload = {
                patient_context: workflowData.patient_context,
                workflow_type: 'patient_context_medpass',
                initiated_by: 'current_physician', // Should come from auth context
                initiated_from: 'patient_dashlet'
            };

            console.log('Creating workflow with payload:', workflowPayload);
            const result = await medpassService.createPatientContextWorkflow(workflowPayload);
            console.log('Workflow creation result:', result);
            
            if (result.success) {
                const workflowId = result.data.workflow_id;
                console.log('Workflow created with ID:', workflowId);
                
                setWorkflowData(prev => ({
                    ...prev,
                    workflow_id: workflowId
                }));
                
                // Log workflow initiation for audit
                try {
                    await medpassService.logWorkflowEvent(workflowId, {
                        event_type: 'workflow_initiated',
                        patient_id: patient.id,
                        step: 'patient_verification',
                        context: 'patient_dashlet_integration'
                    });
                } catch (error) {
                    console.error('Error logging workflow event:', error);
                }
            } else {
                console.error('Failed to create workflow:', result.message);
            }
        } catch (error) {
            console.error('Error initializing workflow:', error);
            message.error('Failed to initialize medication ordering workflow');
        }
    };

    // Handle step completion and progression
    const handleStepComplete = async (stepData, stepKey) => {
        setLoading(true);
        try {
            // Update workflow data
            const updatedWorkflowData = {
                ...workflowData,
                [stepKey]: stepData
            };
            setWorkflowData(updatedWorkflowData);

            // Save step data to backend
            if (workflowData.workflow_id) {
                // Map frontend step keys to backend field names
                const stepMapping = {
                    'patient_context': 'patient_profile',
                    'selected_medications': 'medications',
                    'dosing_instructions': 'medications', // Enhanced medication data with dosing instructions
                    // Monitoring requirements are handled explicitly (pre_conditions/post_conditions/medications)
                    'monitoring_requirements': 'monitoring_requirements'
                };
                
                const backendField = stepMapping[stepKey] || stepKey;
                
                // Initialize updateData object
                let updateData = { current_step: stepKey };
                
                // Special handling for different step types
                let processedStepData = stepData;
                
                if (stepKey === 'patient_context' && stepData.verified) {
                    // Ensure patient profile has verified flag and convert complex objects to strings
                    processedStepData = { 
                        ...stepData, 
                        verified: true,
                        // Convert allergies from objects to strings
                        allergies: stepData.allergies ? stepData.allergies.map(allergy => 
                            typeof allergy === 'string' ? allergy : allergy.allergen || allergy.name || 'Unknown'
                        ) : [],
                        // Convert medical_conditions from objects to strings
                        medical_conditions: stepData.medical_conditions ? stepData.medical_conditions.map(condition => 
                            typeof condition === 'string' ? condition : condition.condition || condition.name || 'Unknown'
                        ) : [],
                        // Convert current_medications from objects to strings
                        current_medications: stepData.current_medications ? stepData.current_medications.map(med => 
                            typeof med === 'string' ? med : med.medication || med.name || 'Unknown'
                        ) : []
                    };
                } else if (stepKey === 'selected_medications') {
                    // Convert medication selection to PrescribedMedication format
                    processedStepData = stepData.map(med => {
                        // Convert strength object to string
                        let strengthStr = '1 tablet';
                        if (med.strength) {
                            if (typeof med.strength === 'string') {
                                strengthStr = med.strength;
                            } else if (typeof med.strength === 'object' && med.strength.value && med.strength.unit) {
                                strengthStr = `${med.strength.value} ${med.strength.unit}`;
                            }
                        }
                        
                        // Convert dosage_form enum to string
                        let dosageFormStr = 'tablet';
                        if (med.dosage_form) {
                            if (typeof med.dosage_form === 'string') {
                                dosageFormStr = med.dosage_form;
                            } else if (typeof med.dosage_form === 'object' && med.dosage_form.value) {
                                dosageFormStr = med.dosage_form.value;
                            }
                        }
                        
                        // Convert route enum to string
                        let routeStr = 'oral';
                        if (med.route) {
                            if (typeof med.route === 'string') {
                                routeStr = med.route;
                            } else if (typeof med.route === 'object' && med.route.value) {
                                routeStr = med.route.value;
                            }
                        }
                        
                        return {
                            medication_id: med.medication_id || med.drug_code,
                            drug_code: med.drug_code,
                            brand_name: med.brand_name || med.medication_name,
                            generic_name: med.generic_name,
                            strength: strengthStr,
                            dosage_form: dosageFormStr,
                            route: routeStr,
                            dose_amount: med.dose_amount || '1 tablet',
                            frequency: med.frequency || 'once daily',
                            duration: med.duration || '7 days',
                            directions: med.directions || 'Take as prescribed',
                            quantity: med.quantity || 30,
                            days_supply: med.days_supply || 30,
                            refills_allowed: med.refills_allowed || 0,
                            refills_remaining: med.refills_remaining || 0,
                            status: 'active'
                        };
                    });
                    
                    // Create scheduling information based on the most common frequency
                    if (processedStepData.length > 0) {
                        // Get the most common frequency from medications
                        const frequencyCounts = {};
                        processedStepData.forEach(med => {
                            frequencyCounts[med.frequency] = (frequencyCounts[med.frequency] || 0) + 1;
                        });
                        
                        const mostCommonFrequency = Object.keys(frequencyCounts).reduce((a, b) => 
                            frequencyCounts[a] > frequencyCounts[b] ? a : b
                        );
                        
                        // Using getDefaultAdministrationTimes function defined at component level
                        
                        // Create scheduling info from step data if available
                        const sd = stepData || {};
                        const formatDateYMD = (d) => {
                            try {
                                if (!d) return null;
                                if (typeof d?.format === 'function') return d.format('YYYY-MM-DD');
                                if (d instanceof Date) return d.toISOString().split('T')[0];
                                if (typeof d === 'string') return d;
                                return null;
                            } catch (e) { return null; }
                        };
                        const schedulingInfo = {
                            start_date: formatDateYMD(sd.start_date) || dayjs().format('YYYY-MM-DD'),
                            end_date: formatDateYMD(sd.end_date),
                            administration_times: sd.administration_times || getDefaultAdministrationTimes(mostCommonFrequency),
                            frequency: sd.frequency || mostCommonFrequency,
                            administration_method: sd.administration_method || 'nurse_administered',
                            administrator_id: sd.administrator_id || null,
                            administrator_name: sd.administrator_name || null,
                            timing_instructions: sd.timing_instructions || null,
                            food_instructions: sd.food_instructions || null,
                            activity_restrictions: sd.activity_restrictions || null,
                            monitoring_schedule: sd.monitoring_schedule || null,
                            follow_up_schedule: sd.follow_up_schedule || null
                        };
                        
                        // Add scheduling info to the update data
                        updateData.scheduling_info = schedulingInfo;
                    }
                } else if (stepKey === 'dosing_instructions') {
                    // Dosing instructions update medications with enhanced scheduling and monitoring
                    if (stepData && stepData.medication_instructions) {
                        processedStepData = stepData.medication_instructions.map(medInstruction => {
                            const normDate = (d) => {
                                try {
                                    if (!d) return null;
                                    if (typeof d?.format === 'function') return d.format('YYYY-MM-DD');
                                    if (d instanceof Date) return dayjs(d).format('YYYY-MM-DD');
                                    if (typeof d === 'string') return d.includes('T') ? dayjs(d).format('YYYY-MM-DD') : d;
                                } catch (_) {}
                                return null;
                            };
                            // Map frontend frequency values to backend format
                            const frequencyMapping = {
                                'once_daily': 'once daily',
                                'twice_daily': 'twice daily', 
                                'three_times_daily': 'three times daily',
                                'four_times_daily': 'four times daily',
                                'every_4_hours': 'every 4 hours',
                                'every_6_hours': 'every 6 hours',
                                'every_8_hours': 'every 8 hours',
                                'every_12_hours': 'every 12 hours',
                                'with_meals': 'with meals',
                                'at_bedtime': 'at bedtime',
                                'as_needed': 'as needed (PRN)'
                            };
                            
                            const mappedFrequency = frequencyMapping[medInstruction.frequency] || medInstruction.frequency;
                            
                            // Derive route from instruction; fallback to explicit field names, then default
                            const routeStr = (typeof medInstruction.route === 'string' && medInstruction.route) 
                                || medInstruction.route?.value 
                                || medInstruction.route_of_administration 
                                || 'oral';

                            return {
                                // Required PrescribedMedication fields
                                medication_id: medInstruction.medication_id,
                                drug_code: medInstruction.medication_id, // Use medication_id as drug_code
                                brand_name: medInstruction.medication_name,
                                generic_name: medInstruction.generic_name,
                                strength: `${medInstruction.strength?.value || '1'}${medInstruction.strength?.unit || 'mg'}`,
                                dosage_form: medInstruction.dosage_form,
                                route: routeStr,
                                dose_amount: `${medInstruction.dose_amount} ${medInstruction.dose_unit || 'tablet(s)'}`,
                                frequency: mappedFrequency,
                                duration: `${medInstruction.duration_days} days`,
                                directions: medInstruction.physician_instructions,
                                quantity: medInstruction.calculated_quantity || 30,
                                days_supply: medInstruction.duration_days || 30,
                                refills_allowed: 0,
                                refills_remaining: 0,
                                status: 'active',
                                
                                // Enhanced fields for scheduling and monitoring
                                duration_days: medInstruction.duration_days,
                                administration_times: medInstruction.administration_times || getDefaultAdministrationTimes(medInstruction.frequency || 'once_daily'),
                                administration_type: medInstruction.administration_type,
                                physician_instructions: medInstruction.physician_instructions,
                                indication: medInstruction.indication,
                                special_notes: medInstruction.special_notes,
                                timing_notes: medInstruction.timing_notes,
                                use_default_timing: medInstruction.use_default_timing,
                                custom_timing_enabled: medInstruction.custom_timing_enabled,
                                calculated_quantity: medInstruction.calculated_quantity,
                                // Carry over start/end dates from instruction with fallback to scheduling_info (normalized to YYYY-MM-DD)
                                start_date: normDate(medInstruction.start_date) || normDate(workflowData.scheduling_info?.start_date) || null,
                                end_date: normDate(medInstruction.end_date) || normDate(workflowData.scheduling_info?.end_date) || null
                            };
                        });
                    } else {
                        // Fallback for legacy format
                        processedStepData = stepData;
                    }
                    
                    // Update scheduling information based on dosing instructions
                    if (processedStepData && processedStepData.length > 0) {
                        // Get the most common frequency from medications
                        const frequencyCounts = {};
                        processedStepData.forEach(med => {
                            if (med.frequency) {
                                frequencyCounts[med.frequency] = (frequencyCounts[med.frequency] || 0) + 1;
                            }
                        });
                        
                        if (Object.keys(frequencyCounts).length > 0) {
                            const mostCommonFrequency = Object.keys(frequencyCounts).reduce((a, b) => 
                                frequencyCounts[a] > frequencyCounts[b] ? a : b
                            );
                            
                            // Get custom administration times from the first medication with custom timing
                            const medicationWithCustomTiming = processedStepData.find(med => 
                                med.administration_times && med.administration_times.length > 0
                            );
                            
                            const customAdministrationTimes = medicationWithCustomTiming?.administration_times || [];
                            
                            // Using getDefaultAdministrationTimes function defined at component level
                            
                            // FIXED: Don't use mostCommonFrequency - this causes all medications to get same schedule
                            // Instead, each medication should have its own administration times based on its own frequency
                            const administrationTimes = customAdministrationTimes.length > 0
                                ? customAdministrationTimes
                                : getDefaultAdministrationTimes('once_daily'); // Default fallback
                            
                            // Derive start/end dates from medication instructions if provided in this step
                            const toYMD = (dStr) => {
                                try {
                                    if (!dStr) return null;
                                    // If moment-like
                                    if (typeof dStr?.format === 'function') return dStr.format('YYYY-MM-DD');
                                    // If Date instance
                                    if (dStr instanceof Date) {
                                        return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai' }).format(dStr);
                                    }
                                    // If string (ISO or YYYY-MM-DD)
                                    if (typeof dStr === 'string') {
                                        if (dStr.includes('T')) {
                                            const d = new Date(dStr);
                                            return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai' }).format(d);
                                        }
                                        return dStr; // assume YYYY-MM-DD
                                    }
                                } catch (e) {}
                                return null;
                            };
                            const instructionDates = (processedStepData || [])
                                .map(m => ({
                                    s: toYMD(m.start_date),
                                    e: toYMD(m.end_date)
                                }))
                                .filter(x => x.s || x.e);
                            let derivedStart = null;
                            let derivedEnd = null;
                            if (instructionDates.length > 0) {
                                const starts = instructionDates.map(x => x.s).filter(Boolean).sort();
                                const ends = instructionDates.map(x => x.e).filter(Boolean).sort();
                                derivedStart = starts.length ? starts[0] : null;
                                derivedEnd = ends.length ? ends[ends.length - 1] : null;
                            }

                            // Create or update scheduling info; prefer values provided in stepData if present, else derive from meds
                            const sd2 = stepData || {};
                            const formatDateYMD2 = (d) => {
                                try {
                                    if (!d) return null;
                                    if (typeof d?.format === 'function') return d.format('YYYY-MM-DD');
                                    if (d instanceof Date) return d.toISOString().split('T')[0];
                                    if (typeof d === 'string') return d;
                                    return null;
                                } catch (e) { return null; }
                            };
                            const schedulingInfo = {
                                start_date: formatDateYMD2(sd2.start_date) || derivedStart || new Date().toISOString().split('T')[0],
                                end_date: formatDateYMD2(sd2.end_date) || derivedEnd,
                                administration_times: sd2.administration_times || administrationTimes,
                                frequency: sd2.frequency || 'once_daily', // FIXED: Don't use mostCommonFrequency
                                administration_method: sd2.administration_method || 'nurse_administered',
                                administrator_id: sd2.administrator_id || null,
                                administrator_name: sd2.administrator_name || null,
                                timing_instructions: sd2.timing_instructions || null,
                                food_instructions: sd2.food_instructions || null,
                                activity_restrictions: sd2.activity_restrictions || null,
                                monitoring_schedule: sd2.monitoring_schedule || null,
                                follow_up_schedule: sd2.follow_up_schedule || null
                            };
                            
                            // Add scheduling info to the update data
                            updateData.scheduling_info = schedulingInfo;
                        }
                    }
                } else if (stepKey === 'monitoring_requirements') {
                    // Convert monitoring requirements to PrePostCondition format
                    if (stepData && typeof stepData === 'object') {
                        console.log('Processing monitoring requirements:', stepData);
                        
                        // Handle the actual monitoring data structure from MonitoringReviewStep
                        const monitoringData = stepData;
                        
                        processedStepData = [];
                        
                        // Create pre-condition for pre-administration vitals
                        if (monitoringData.pre_administration_vitals && monitoringData.pre_administration_vitals.length > 0) {
                            const preCondition = {
                                condition_id: `pre_monitoring_${Date.now()}`,
                                condition_type: 'pre',
                                description: 'Pre-administration monitoring requirement',
                                required_vitals: monitoringData.pre_administration_vitals,
                                vital_ranges: {},
                                assessment_questions: [],
                                patient_instructions: monitoringData.monitoring_notes || '',
                                caregiver_instructions: monitoringData.special_precautions || '',
                                monitoring_frequency: monitoringData.monitoring_frequency || 'daily',
                                alert_thresholds: {}
                            };
                            
                            console.log('Created pre-condition:', preCondition);
                            processedStepData.push(preCondition);
                        }
                        
                        // Create post-condition for post-administration vitals
                        if (monitoringData.post_administration_vitals && monitoringData.post_administration_vitals.length > 0) {
                            const postCondition = {
                                condition_id: `post_monitoring_${Date.now()}`,
                                condition_type: 'post',
                                description: 'Post-administration monitoring requirement',
                                required_vitals: monitoringData.post_administration_vitals,
                                vital_ranges: {},
                                assessment_questions: [],
                                patient_instructions: monitoringData.monitoring_notes || '',
                                caregiver_instructions: monitoringData.special_precautions || '',
                                monitoring_frequency: monitoringData.monitoring_frequency || 'daily',
                                alert_thresholds: {}
                            };
                            
                            console.log('Created post-condition:', postCondition);
                            processedStepData.push(postCondition);
                        }
                        
                        console.log('Final processed monitoring data:', processedStepData);
                    } else {
                        // If stepData is a string or simple value, create a basic monitoring condition
                        processedStepData = [{
                            condition_id: `monitoring_${Date.now()}`,
                            condition_type: 'pre',
                            description: typeof stepData === 'string' ? stepData : 'General monitoring required',
                            required_vitals: [],
                            vital_ranges: {},
                            assessment_questions: [],
                            patient_instructions: null,
                            caregiver_instructions: null,
                            monitoring_frequency: 'daily',
                            alert_thresholds: {}
                        }];
                    }
                    
                    // Separate pre_conditions and post_conditions
                    const preConditions = processedStepData.filter(condition => condition.condition_type === 'pre');
                    const postConditions = processedStepData.filter(condition => condition.condition_type === 'post');
                    
                    if (preConditions.length > 0) {
                        updateData.pre_conditions = preConditions;
                    }
                    
                    if (postConditions.length > 0) {
                        updateData.post_conditions = postConditions;
                    }
                    
                    // Also update existing medications with monitoring data
                    if (stepData && stepData.monitoring_enabled && updatedWorkflowData.dosing_instructions?.medication_instructions) {
                        const updatedMedications = updatedWorkflowData.dosing_instructions.medication_instructions.map(medInstruction => ({
                            ...medInstruction,
                            // Add monitoring vitals to each medication (deduplicated)
                            pre_administration_vitals: Array.from(new Set([...
                                (Array.isArray(medInstruction.pre_administration_vitals) ? medInstruction.pre_administration_vitals : []),
                                ...(Array.isArray(stepData.pre_administration_vitals) ? stepData.pre_administration_vitals : [])
                            ])),
                            post_administration_vitals: Array.from(new Set([...
                                (Array.isArray(medInstruction.post_administration_vitals) ? medInstruction.post_administration_vitals : []),
                                ...(Array.isArray(stepData.post_administration_vitals) ? stepData.post_administration_vitals : [])
                            ])),
                            monitoring_frequency: stepData.monitoring_frequency,
                            monitoring_duration: stepData.monitoring_duration,
                            monitoring_notes: stepData.monitoring_notes
                        }));
                        
                        // Update medications field with enhanced monitoring data
                        updateData.medications = updatedMedications.map(medInstruction => {
                            const frequencyMapping = {
                                'once_daily': 'once daily',
                                'twice_daily': 'twice daily', 
                                'three_times_daily': 'three times daily',
                                'four_times_daily': 'four times daily',
                                'every_4_hours': 'every 4 hours',
                                'every_6_hours': 'every 6 hours',
                                'every_8_hours': 'every 8 hours',
                                'every_12_hours': 'every 12 hours',
                                'with_meals': 'with meals',
                                'at_bedtime': 'at bedtime',
                                'as_needed': 'as needed (PRN)'
                            };
                            
                            const mappedFrequency = frequencyMapping[medInstruction.frequency] || medInstruction.frequency;
                            
                            // Derive route as above for consistency
                            const routeStr = (typeof medInstruction.route === 'string' && medInstruction.route) 
                                || medInstruction.route?.value 
                                || medInstruction.route_of_administration 
                                || 'oral';

                            return {
                                // Required PrescribedMedication fields
                                medication_id: medInstruction.medication_id,
                                drug_code: medInstruction.medication_id,
                                brand_name: medInstruction.medication_name,
                                generic_name: medInstruction.generic_name,
                                strength: `${medInstruction.strength?.value || '1'}${medInstruction.strength?.unit || 'mg'}`,
                                dosage_form: medInstruction.dosage_form,
                                route: routeStr,
                                dose_amount: `${medInstruction.dose_amount} ${medInstruction.dose_unit || 'tablet(s)'}`,
                                frequency: mappedFrequency,
                                duration: `${medInstruction.duration_days} days`,
                                directions: medInstruction.physician_instructions,
                                quantity: medInstruction.calculated_quantity || 30,
                                days_supply: medInstruction.duration_days || 30,
                                refills_allowed: 0,
                                refills_remaining: 0,
                                status: 'active',
                                
                                // Enhanced fields for scheduling and monitoring
                                duration_days: medInstruction.duration_days,
                                administration_times: medInstruction.administration_times || getDefaultAdministrationTimes(medInstruction.frequency || 'once_daily'),
                                administration_type: medInstruction.administration_type,
                                physician_instructions: medInstruction.physician_instructions,
                                indication: medInstruction.indication,
                                special_notes: medInstruction.special_notes,
                                timing_notes: medInstruction.timing_notes,
                                use_default_timing: medInstruction.use_default_timing,
                                custom_timing_enabled: medInstruction.custom_timing_enabled,
                                calculated_quantity: medInstruction.calculated_quantity,

                                // Carry over start/end dates to each medication with fallback to scheduling_info
                                start_date: (typeof medInstruction.start_date?.format === 'function')
                                    ? medInstruction.start_date.format('YYYY-MM-DD')
                                    : (medInstruction.start_date && medInstruction.start_date.includes && medInstruction.start_date.includes('T')
                                        ? medInstruction.start_date.split('T')[0]
                                        : medInstruction.start_date || workflowData.scheduling_info?.start_date || null),
                                end_date: (typeof medInstruction.end_date?.format === 'function')
                                    ? medInstruction.end_date.format('YYYY-MM-DD')
                                    : (medInstruction.end_date && medInstruction.end_date.includes && medInstruction.end_date.includes('T')
                                        ? medInstruction.end_date.split('T')[0]
                                        : medInstruction.end_date || workflowData.scheduling_info?.end_date || null),
                                
                                // Monitoring vitals from monitoring step (already deduped above)
                                pre_administration_vitals: medInstruction.pre_administration_vitals || [],
                                post_administration_vitals: medInstruction.post_administration_vitals || [],
                                monitoring_frequency: medInstruction.monitoring_frequency,
                                monitoring_duration: medInstruction.monitoring_duration,
                                monitoring_notes: medInstruction.monitoring_notes
                            };
                        });
                    }
                }
                
                // Add the processed step data to updateData, except for monitoring where we already set explicit fields
                if (stepKey !== 'monitoring_requirements') {
                    updateData[backendField] = processedStepData;
                } else {
                    // For observability, persist a compact monitoring_requirements snapshot
                    updateData.monitoring_requirements = {
                        monitoring_enabled: !!(stepData && stepData.monitoring_enabled),
                        pre_administration_vitals: Array.isArray(stepData?.pre_administration_vitals) ? Array.from(new Set(stepData.pre_administration_vitals)) : [],
                        post_administration_vitals: Array.isArray(stepData?.post_administration_vitals) ? Array.from(new Set(stepData.post_administration_vitals)) : [],
                        monitoring_frequency: stepData?.monitoring_frequency,
                        monitoring_duration: stepData?.monitoring_duration,
                        monitoring_notes: stepData?.monitoring_notes,
                        special_precautions: stepData?.special_precautions
                    };
                }
                
                // Update workflow with processed data
                console.log(`Updating workflow with ${stepKey} data:`, updateData);
                console.log(`Current workflowData.scheduling_info:`, workflowData?.scheduling_info);
                console.log(`updateData.hasOwnProperty('scheduling_info'):`, updateData.hasOwnProperty('scheduling_info'));
                console.log(`updateData.scheduling_info:`, updateData.scheduling_info);
                
                // CRITICAL FIX: Always preserve scheduling_info for monitoring step
                if (stepKey === 'monitoring_requirements' && workflowData?.scheduling_info) {
                    console.log('✅ PRESERVING scheduling_info for monitoring step');
                    updateData.scheduling_info = workflowData.scheduling_info;
                }
                
                // For monitoring step, ensure medications have dates by fetching from latest backend state
                if (stepKey === 'monitoring_requirements' && (!workflowData?.scheduling_info)) {
                    console.log('🔍 Monitoring step: fetching latest scheduling_info from backend');
                    try {
                        const latestWorkflow = await medpassService.getPatientContextWorkflow(workflowData.workflow_id);
                        if (latestWorkflow.success && latestWorkflow.data?.scheduling_info) {
                            console.log('📡 Retrieved scheduling_info for monitoring step:', latestWorkflow.data.scheduling_info);
                            // Add scheduling dates to any medications that don't have them
                            if (updateData.medications && Array.isArray(updateData.medications)) {
                                updateData.medications.forEach(med => {
                                    if (!med.start_date && latestWorkflow.data.scheduling_info.start_date) {
                                        med.start_date = latestWorkflow.data.scheduling_info.start_date;
                                        console.log(`📅 Added start_date to ${med.brand_name}: ${med.start_date}`);
                                    }
                                    if (!med.end_date && latestWorkflow.data.scheduling_info.end_date) {
                                        med.end_date = latestWorkflow.data.scheduling_info.end_date;
                                        console.log(`📅 Added end_date to ${med.brand_name}: ${med.end_date}`);
                                    }
                                });
                            }
                        }
                    } catch (error) {
                        console.error('❌ Failed to fetch scheduling_info for monitoring step:', error);
                    }
                }

                // Log medication dates for debugging
                if (updateData.medications && Array.isArray(updateData.medications)) {
                    updateData.medications.forEach((med, index) => {
                        console.log(`✅ Medication ${index + 1} (${med.brand_name}) dates:`, {
                            start_date: med.start_date,
                            end_date: med.end_date,
                            has_start: !!med.start_date,
                            has_end: !!med.end_date
                        });
                    });
                }
                
                console.log(`Final updateData for ${stepKey}:`, updateData);

                const updateResult = await medpassService.updatePatientContextWorkflow(
                    workflowData.workflow_id,
                    updateData
                );
                
                console.log(`Update result:`, updateResult);
                
                if (!updateResult.success) {
                    console.error(`Update failed:`, updateResult.message);
                }

                if (updateResult.success) {
                    // Update local workflowData with scheduling_info from backend response
                    if (updateResult.data?.scheduling_info && !workflowData.scheduling_info) {
                        console.log('📡 Updating local scheduling_info from backend response');
                        setWorkflowData(prev => ({
                            ...prev,
                            scheduling_info: updateResult.data.scheduling_info
                        }));
                    }
                    
                    // Log step completion
                    await medpassService.logWorkflowEvent(workflowData.workflow_id, {
                        event_type: 'step_completed',
                        patient_id: patient.id,
                        step: stepKey,
                        data_snapshot: stepData
                    });

                    message.success('Step completed successfully');
                    
                    // Auto-advance to next step (except for the last step)
                    if (currentStep < steps.length - 1) {
                        setCurrentStep(currentStep + 1);
                    }
                    
                    return true;
                } else {
                    message.error(updateResult.message || 'Failed to save step data');
                    return false;
                }
            }
        } catch (error) {
            console.error('Error completing step:', error);
            message.error('Failed to complete step');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const nextStep = async () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Handle final workflow submission
    const handleWorkflowSubmit = async () => {
        setLoading(true);
        try {
            // Perform final validation
            const validationResult = await medpassService.validatePatientContextWorkflow(
                workflowData.workflow_id
            );

            if (!validationResult.success || !validationResult.data.is_valid) {
                message.error('Workflow validation failed. Please review all steps.');
                setLoading(false);
                return;
            }

            // Submit medications to patient record using standard endpoint
            if (workflowData.dosing_instructions?.medication_instructions && 
                workflowData.dosing_instructions.medication_instructions.length > 0) {
                
                console.log('Submitting medications to patient record...');
                console.log('Dosing instructions:', workflowData.dosing_instructions);
                console.log('Monitoring requirements:', workflowData.monitoring_requirements);
                
                try {
                    const medicationInstructions = workflowData.dosing_instructions.medication_instructions;
                    const monitoringRequirements = workflowData.monitoring_requirements || {};
                    
                    // Debug each medication's administration times
                    medicationInstructions.forEach((med, index) => {
                        console.log(`Medication ${index} (${med.medication_name}):`, {
                            frequency: med.frequency,
                            administration_times: med.administration_times,
                            use_default_timing: med.use_default_timing,
                            custom_timing_enabled: med.custom_timing_enabled
                        });
                    });
                    
                    // Add each medication using the standard endpoint
                    let successCount = 0;
                    
                    for (const medInstruction of medicationInstructions) {
                        // Map frontend frequency values to backend format
                        const frequencyMapping = {
                            'once_daily': 'once daily',
                            'twice_daily': 'twice daily', 
                            'three_times_daily': 'three times daily',
                            'four_times_daily': 'four times daily',
                            'every_4_hours': 'every 4 hours',
                            'every_6_hours': 'every 6 hours',
                            'every_8_hours': 'every 8 hours',
                            'every_12_hours': 'every 12 hours',
                            'with_meals': 'with meals',
                            'at_bedtime': 'at bedtime',
                            'as_needed': 'as needed (PRN)'
                        };
                        
                        // Helper function to safely convert dates to YYYY-MM-DD format
                        const safeFormatDate = (dateValue) => {
                            try {
                                if (!dateValue) return null;
                                
                                // Handle moment.js objects
                                if (typeof dateValue?.format === 'function') {
                                    return dateValue.format('YYYY-MM-DD');
                                }
                                
                                // Handle Date objects
                                if (dateValue instanceof Date) {
                                    const year = dateValue.getFullYear();
                                    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
                                    const day = String(dateValue.getDate()).padStart(2, '0');
                                    return `${year}-${month}-${day}`;
                                }
                                
                                // Handle string dates (ISO or YYYY-MM-DD)
                                if (typeof dateValue === 'string') {
                                    // If it's already in YYYY-MM-DD format, return as is
                                    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                                        return dateValue;
                                    }
                                    
                                    // CRITICAL FIX: Handle ISO datetime strings without timezone conversion
                                    // Extract date part from ISO strings like '2025-08-30T18:30:00.000Z'
                                    if (dateValue.includes('T')) {
                                        const datePart = dateValue.split('T')[0];
                                        // Validate the extracted date part
                                        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
                                            console.log(`[safeFormatDate] Fixed timezone issue: ${dateValue} → ${datePart}`);
                                            return datePart;
                                        }
                                    }
                                    
                                    // Try to parse and convert (but avoid timezone issues)
                                    const parsed = new Date(dateValue);
                                    if (!isNaN(parsed.getTime())) {
                                        const year = parsed.getFullYear();
                                        const month = String(parsed.getMonth() + 1).padStart(2, '0');
                                        const day = String(parsed.getDate()).padStart(2, '0');
                                        return `${year}-${month}-${day}`;
                                    }
                                }
                                
                                console.warn('Unable to parse date value:', dateValue);
                                return null;
                            } catch (error) {
                                console.error('Error formatting date:', error, 'Date value:', dateValue);
                                return null;
                            }
                        };

                        // Create medication data for the standard endpoint
                        const medicationData = {
                            // Basic required fields
                            medication: medInstruction.medication_name || medInstruction.medication || 'Unknown Medication',
                            dosage: `${medInstruction.dose_amount || '1'} ${medInstruction.dose_unit || 'tablet(s)'}`,
                            frequency: frequencyMapping[medInstruction.frequency] || medInstruction.frequency || 'once daily',
                            route: (typeof medInstruction.route === 'string' && medInstruction.route)
                                || medInstruction.route?.value
                                || medInstruction.route_of_administration
                                || 'oral',

                            // NABIDH fields - medication_id, strength, and dosage_form for HL7 ORM messages
                            medication_id: medInstruction.medication_id || null,
                            strength: medInstruction.strength
                                ? (typeof medInstruction.strength === 'string'
                                    ? medInstruction.strength
                                    : medInstruction.strength.value && medInstruction.strength.unit
                                        ? `${medInstruction.strength.value}${medInstruction.strength.unit}`
                                        : medInstruction.strength.value || null)
                                : null,
                            dosage_form: medInstruction.dosage_form || null,

                            // Date fields - ensure proper YYYY-MM-DD format for backend
                            // Try medication-specific dates first, then fall back to workflow scheduling info
                            start_date: safeFormatDate(medInstruction.start_date) || 
                                       safeFormatDate(workflowData.scheduling_info?.start_date),
                            end_date: safeFormatDate(medInstruction.end_date) || 
                                     safeFormatDate(workflowData.scheduling_info?.end_date),
                            
                            // Enhanced scheduling fields - Ensure proper data types
                            dose_amount: String(medInstruction.dose_amount || '1'),
                            duration_days: Number(medInstruction.duration_days) || 30,
                            administration_times: medInstruction.administration_times || [],
                            administration_type: medInstruction.administration_type || 'routine',
                            physician_instructions: medInstruction.physician_instructions || '',
                            indication: medInstruction.indication || 'None',
                            special_notes: medInstruction.special_notes || 'None',
                            timing_notes: medInstruction.timing_notes || '',
                            
                            // Vital signs monitoring - prefer medication-level if present, fallback to workflow-level
                            pre_administration_vitals: Array.from(new Set([...
                                (Array.isArray(medInstruction.pre_administration_vitals) ? medInstruction.pre_administration_vitals : []),
                                ...(Array.isArray(monitoringRequirements.pre_administration_vitals) ? monitoringRequirements.pre_administration_vitals : [])
                            ])),
                            post_administration_vitals: Array.from(new Set([...
                                (Array.isArray(medInstruction.post_administration_vitals) ? medInstruction.post_administration_vitals : []),
                                ...(Array.isArray(monitoringRequirements.post_administration_vitals) ? monitoringRequirements.post_administration_vitals : [])
                            ])),
                            monitoring_frequency: monitoringRequirements.monitoring_frequency || 'daily',
                            monitoring_duration: Number(monitoringRequirements.monitoring_duration) || 7,
                            monitoring_notes: monitoringRequirements.monitoring_notes || 'None',
                            
                            // Timing preferences - Fix: Only use default timing if no custom times are provided
                            use_default_timing: !medInstruction.administration_times || medInstruction.administration_times.length === 0,
                            custom_timing_enabled: medInstruction.administration_times && medInstruction.administration_times.length > 0
                        };
                        
                        // Enhanced date validation and logging
                        const dateValidation = {
                            medication_instruction_dates: {
                                raw_start_date: medInstruction.start_date,
                                raw_end_date: medInstruction.end_date,
                                start_date_type: typeof medInstruction.start_date,
                                end_date_type: typeof medInstruction.end_date
                            },
                            workflow_scheduling_dates: {
                                raw_start_date: workflowData.scheduling_info?.start_date,
                                raw_end_date: workflowData.scheduling_info?.end_date,
                                start_date_type: typeof workflowData.scheduling_info?.start_date,
                                end_date_type: typeof workflowData.scheduling_info?.end_date
                            },
                            final_medication_data_dates: {
                                formatted_start_date: medicationData.start_date,
                                formatted_end_date: medicationData.end_date,
                                duration_days: medicationData.duration_days,
                                start_date_valid: !!medicationData.start_date,
                                end_date_valid: !!medicationData.end_date
                            }
                        };

                        console.log(`Adding medication: ${medicationData.medication}`, {
                            ...medicationData,
                            date_validation_details: dateValidation,
                            administration_times_check: medicationData.administration_times,
                            vital_signs_check: {
                                pre: medicationData.pre_administration_vitals,
                                post: medicationData.post_administration_vitals
                            }
                        });

                        // Validation warning for missing dates
                        if (!medicationData.start_date && !medicationData.end_date) {
                            console.warn(`WARNING: No start or end dates found for medication ${medicationData.medication}`);
                            console.warn('Date sources checked:', dateValidation);
                        }
                        
                        // Validate vital signs data before sending
                        if (medicationData.pre_administration_vitals && !Array.isArray(medicationData.pre_administration_vitals)) {
                            console.warn('Pre-administration vitals is not an array:', medicationData.pre_administration_vitals);
                            medicationData.pre_administration_vitals = [];
                        }
                        
                        if (medicationData.post_administration_vitals && !Array.isArray(medicationData.post_administration_vitals)) {
                            console.warn('Post-administration vitals is not an array:', medicationData.post_administration_vitals);
                            medicationData.post_administration_vitals = [];
                        }
                        
                        // Ensure administration times is always an array
                        if (!Array.isArray(medicationData.administration_times)) {
                            console.warn('Administration times is not an array:', medicationData.administration_times);
                            medicationData.administration_times = [];
                        }
                        
                        // Validate administration_type matches expected pattern
                        if (medicationData.administration_type && !['routine', 'prn'].includes(medicationData.administration_type)) {
                            console.warn('Invalid administration_type:', medicationData.administration_type);
                            medicationData.administration_type = 'routine';
                        }
                        
                        // Validate duration_days is within range (1-365)
                        if (medicationData.duration_days < 1 || medicationData.duration_days > 365) {
                            console.warn('Duration days out of range:', medicationData.duration_days);
                            medicationData.duration_days = Math.max(1, Math.min(365, medicationData.duration_days));
                        }
                        
                        // Validate monitoring_duration is within range (1-365)
                        if (medicationData.monitoring_duration && (medicationData.monitoring_duration < 1 || medicationData.monitoring_duration > 365)) {
                            console.warn('Monitoring duration out of range:', medicationData.monitoring_duration);
                            medicationData.monitoring_duration = Math.max(1, Math.min(365, medicationData.monitoring_duration));
                        }
                        
                        const result = await patientService.addPatientMedication(patient.id, medicationData);
                        
                        if (result.success || result.message) {
                            successCount++;
                        }
                    }
                    
                    if (successCount > 0) {
                        message.success(`Successfully added ${successCount} medication(s) with detailed scheduling and monitoring`);
                    }
                    
                } catch (medicationError) {
                    console.error('Error adding medications to patient record:', medicationError);
                    message.error('Failed to add medications to patient record');
                    // Continue with workflow submission even if medication addition fails
                }
            }

            // Then submit workflow for processing
            const submitResult = await medpassService.submitPatientContextWorkflow(
                workflowData.workflow_id,
                {
                    submitted_by: 'current_physician',
                    attestation: workflowData.physician_attestation,
                    final_review_completed: true
                }
            );

            if (submitResult.success) {
                // Log successful submission
                await medpassService.logWorkflowEvent(workflowData.workflow_id, {
                    event_type: 'workflow_submitted',
                    patient_id: patient.id,
                    outcome: 'success'
                });

                message.success('Medication order workflow submitted successfully');
                onComplete(submitResult.data);
            } else {
                message.error(submitResult.message || 'Failed to submit medication order');
            }
        } catch (error) {
            console.error('Error submitting workflow:', error);
            message.error('Failed to submit medication order');
        } finally {
            setLoading(false);
        }
    };

    // Calculate workflow progress
    const calculateProgress = () => {
        const completedSteps = Object.keys(workflowData).filter(key => 
            ['patient_context', 'selected_medications', 'dosing_instructions', 'monitoring_requirements'].includes(key) &&
            workflowData[key] && 
            (Array.isArray(workflowData[key]) ? workflowData[key].length > 0 : Object.keys(workflowData[key]).length > 0)
        ).length;
        
        return Math.round((completedSteps / 4) * 100);
    };

    // Render current step component
    const renderCurrentStep = () => {
        const stepProps = {
            patient,
            workflowData,
            onStepComplete: handleStepComplete,
            onNext: nextStep,
            onPrev: prevStep,
            loading
        };

        switch (currentStep) {
            case 0:
                return (
                    <PatientVerificationStep 
                        {...stepProps}
                        onVerified={(verificationData) => 
                            handleStepComplete(verificationData, 'patient_context')
                        }
                    />
                );
            case 1:
                return (
                    <DOHMedicationSelectionStep 
                        {...stepProps}
                        onMedicationsSelected={(medications) => 
                            handleStepComplete(medications, 'selected_medications')
                        }
                    />
                );
            case 2:
                return (
                    <DosingInstructionsStep 
                        {...stepProps}
                        onInstructionsComplete={(instructions) => 
                            handleStepComplete(instructions, 'dosing_instructions')
                        }
                    />
                );
            case 3:
                return (
                    <MonitoringReviewStep 
                        {...stepProps}
                        onReviewComplete={(reviewData) => 
                            handleStepComplete(reviewData, 'monitoring_requirements')
                        }
                        onWorkflowSubmit={handleWorkflowSubmit}
                    />
                );
            default:
                return null;
        }
    };

    // Render workflow header with patient context
    const renderWorkflowHeader = () => (
        <Card className="workflow-header" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]} align="middle">
                <Col xs={24} md={12}>
                    <Space direction="vertical" size="small">
                        <div>
                            <strong>Patient:</strong> {patient?.full_name}
                        </div>
                        <div>
                            <strong>MRN:</strong> {patient?.mrn}
                        </div>
                        <div>
                            <strong>Age/Gender:</strong> {patient?.age} years, {patient?.gender}
                        </div>
                    </Space>
                </Col>
                
                <Col xs={24} md={6} style={{ textAlign: 'center' }}>
                    <Progress
                        type="circle"
                        percent={calculateProgress()}
                        size={80}
                        status={calculateProgress() === 100 ? 'success' : 'active'}
                    />
                    <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                        Workflow Progress
                    </div>
                </Col>

                <Col xs={24} md={6}>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {workflowData.safety_validated && (
                            <Alert
                                message="Safety Validated"
                                type="success"
                                size="small"
                                showIcon
                            />
                        )}
                        
                        {patient?.allergies?.length > 0 && (
                            <Alert
                                message={`${patient.allergies.length} Known Allergy(s)`}
                                type="warning"
                                size="small"
                                showIcon
                            />
                        )}
                    </Space>
                </Col>
            </Row>
        </Card>
    );

    // Render action buttons
    const renderActionButtons = () => {
        const isLastStep = currentStep === steps.length - 1;
        const canProceed = workflowData.patient_context?.verified || currentStep > 0;

        return (
            <Card style={{ marginTop: 16 }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Button onClick={onCancel} size="large">
                            Cancel Order
                        </Button>
                    </Col>

                    <Col>
                        <Space>
                            {currentStep > 0 && (
                                <Button 
                                    onClick={prevStep} 
                                    size="large"
                                    disabled={loading}
                                >
                                    Previous
                                </Button>
                            )}
                            
                            
                            {isLastStep && (
                                <Button 
                                    type="primary" 
                                    onClick={handleWorkflowSubmit}
                                    size="large"
                                    loading={loading}
                                    icon={<CheckCircleOutlined />}
                                    disabled={!workflowData.physician_attestation}
                                >
                                    Submit Order
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>
            </Card>
        );
    };

    return (
        <div className="patient-context-medpass-workflow">
            {/* Patient Context Header */}
            {renderWorkflowHeader()}
            
            {/* Workflow Steps Progress */}
            <Card style={{ marginBottom: 16 }}>
                <Steps 
                    current={currentStep} 
                    size="small"
                    responsive={false}
                >
                    {steps.map((step, index) => (
                        <Step
                            key={step.key}
                            title={step.title}
                            description={step.description}
                            icon={step.icon}
                            status={index < currentStep ? 'finish' : index === currentStep ? 'process' : 'wait'}
                        />
                    ))}
                </Steps>
            </Card>

            {/* Current Step Content */}
            <div className="workflow-step-content">
                {renderCurrentStep()}
            </div>

            {/* Action Buttons */}
            {renderActionButtons()}
        </div>
    );
};

export default PatientContextMedPassWorkflow;